// Chỉ tiêu kiểm tra của phiếu QC: kiểu dữ liệu + chuẩn hoá từ mẫu / phiếu đã lưu

// Nhãn hiển thị kết quả chỉ tiêu (giá trị lưu giữ nguyên PASS / FAIL / NA)
export const QC_RESULT_LABELS: Record<string, string> = {
  PASS: "Đạt",
  FAIL: "Không đạt",
  NA: "N/A",
  PENDING: "Chưa kiểm",
};

export interface IsoCriterionResult {
  id: string;
  name: string;
  /** Tên hiển thị ngắn (mẫu v+1); thiếu thì dùng name */
  shortName?: string;
  category?: string;
  /** IPQC: giai đoạn kiểm (SETUP / PATROL / BOTH) và có đo lại ở mỗi lần kiểm tra lặp lại */
  stage?: "SETUP" | "PATROL" | "BOTH";
  repeat?: boolean;
  type: "QUANTITATIVE" | "QUALITATIVE" | "EVALUATION";
  method?: string;
  toolUsed?: string;
  verificationSource?: "SHOPFLOOR_MEASURED" | "SUPPLIER_COA" | "EXTERNAL_LAB";
  unit?: string;
  minValue?: number;
  maxValue?: number;
  targetValue?: number;
  standard?: string;
  defectType?: "CRITICAL" | "MAJOR" | "MINOR";
  required?: boolean;
  measuredValue?: number | string | null;
  result: "PASS" | "FAIL" | "NA" | "PENDING";
  note?: string;
}

export interface NormalizeChecklistOptions {
  /** Phiếu mới (IQC/FQC): chỉ tiêu chưa chấm để "Chưa kiểm", định lượng không điền sẵn số chuẩn */
  pendingByDefault?: boolean;
}

export function normalizeChecklistItems(
  templateCriteria: any[],
  existingChecklist: any,
  options: NormalizeChecklistOptions = {}
): IsoCriterionResult[] {
  const pendingByDefault = Boolean(options.pendingByDefault);
  const existingArray: any[] = Array.isArray(existingChecklist)
    ? existingChecklist
    : Array.isArray(existingChecklist?.items)
      ? existingChecklist.items
      : [];

  const existingMap = new Map<string, any>();
  for (const item of existingArray) {
    if (item?.id) existingMap.set(String(item.id), item);
    if (item?.name) existingMap.set(String(item.name), item);
  }

  // Legacy key-value object support
  const isLegacyRecord =
    existingChecklist &&
    typeof existingChecklist === "object" &&
    !Array.isArray(existingChecklist) &&
    !Array.isArray(existingChecklist.items);

  if (!Array.isArray(templateCriteria) || templateCriteria.length === 0) {
    if (existingArray.length > 0) return existingArray;
    return [];
  }

  return templateCriteria.map((c: any, idx: number) => {
    const id = c.id || `crit_${idx + 1}`;
    const prev = existingMap.get(id) || existingMap.get(c.name);
    let legacyResult: "PASS" | "FAIL" | "NA" | "PENDING" = pendingByDefault && !isLegacyRecord ? "PENDING" : "PASS";
    if (isLegacyRecord && id in existingChecklist) {
      const v = existingChecklist[id];
      if (v === false || v === "FAIL" || v === "FAILED") legacyResult = "FAIL";
      else if (v === "NA") legacyResult = "NA";
      else legacyResult = "PASS";
    }

    const type =
      c.type === "QUANTITATIVE" || c.type === "EVALUATION"
        ? c.type
        : "QUALITATIVE";

    return {
      id,
      name: c.name || `Chỉ tiêu #${idx + 1}`,
      shortName: c.shortName || prev?.shortName || undefined,
      category: c.category || (type === "QUANTITATIVE" ? "MEASUREMENT" : "VISUAL"),
      ...(c.stage || prev?.stage ? { stage: c.stage || prev?.stage } : {}),
      ...(c.repeat || prev?.repeat ? { repeat: true } : {}),
      type,
      method: prev?.method || c.method || "",
      toolUsed: prev?.toolUsed || c.toolUsed || "",
      verificationSource:
        prev?.verificationSource ||
        c.verificationSource ||
        "SHOPFLOOR_MEASURED",
      unit: c.unit || "",
      minValue: c.minValue !== undefined && c.minValue !== null ? Number(c.minValue) : undefined,
      maxValue: c.maxValue !== undefined && c.maxValue !== null ? Number(c.maxValue) : undefined,
      targetValue: c.targetValue !== undefined && c.targetValue !== null ? Number(c.targetValue) : undefined,
      standard: c.standard || c.description || "",
      defectType: c.defectType || c.severity || "MAJOR",
      required: c.required !== false,
      measuredValue:
        prev?.measuredValue !== undefined
          ? prev.measuredValue
          : !pendingByDefault && c.targetValue !== undefined && c.targetValue !== null
            ? c.targetValue
            : "",
      result: prev?.result || legacyResult || "PASS",
      note: prev?.note || "",
    };
  });
}


/**
 * Danh sách chỉ tiêu đã lưu trên phiếu. Chấp nhận mảng, { items: [...] } và dạng object
 * do "Chờ gom xe" trộn cờ readyForDispatch vào mảng ({ "0": {...}, "1": {...}, readyForDispatch: true }).
 */
export function checklistItemsOf(raw: any): any[] {
  if (!raw) return [];
  let v = raw;
  if (typeof v === "string") {
    try {
      v = JSON.parse(v);
    } catch {
      return [];
    }
  }
  if (Array.isArray(v)) return v;
  if (Array.isArray(v?.items)) return v.items;
  if (v && typeof v === "object") {
    return Object.keys(v)
      .filter((k) => /^\d+$/.test(k))
      .sort((a, b) => Number(a) - Number(b))
      .map((k) => v[k])
      .filter((it) => it && typeof it === "object" && (it.name || it.id));
  }
  return [];
}

/**
 * Chỉ tiêu bắt buộc chưa chấm (cùng quy tắc máy chủ, qc-verdict.util.ts): còn chỉ tiêu này thì biên bản Đạt được lưu
 * ở trạng thái Đang kiểm. Dùng để giải thích cho người kiểm (FB-DKF4Y7) — chỉ tiêu có khoảng số (VD Độ ẩm 5,5–10 %)
 * phải nhập số đo, [Tất cả Đạt] không tự điền.
 */
export function pendingRequiredCriteria(items: any[]): Array<{ name: string; needsMeasure: boolean; range: string }> {
  return (items || [])
    .filter((c) => c && c.required !== false && (!c.result || String(c.result).toUpperCase() === "PENDING"))
    .map((c) => {
      const min = c.minValue ?? null;
      const max = c.maxValue ?? null;
      const needsMeasure = min !== null || max !== null;
      const fmt = (v: any) => String(v).replace(".", ",");
      const range = needsMeasure ? `${min !== null ? fmt(min) : "…"}–${max !== null ? fmt(max) : "…"}${c.unit ? ` ${c.unit}` : ""}` : "";
      return { name: String(c.name || c.id || "Chỉ tiêu"), needsMeasure, range };
    });
}

/** Câu ngắn liệt kê chỉ tiêu còn thiếu: "Độ ẩm (nhập số đo 5,5–10 %), Mã vạch" */
export function describePendingCriteria(pending: ReturnType<typeof pendingRequiredCriteria>, max = 3): string {
  const shown = pending.slice(0, max).map((p) => (p.needsMeasure ? `${p.name} (nhập số đo ${p.range})` : p.name));
  return shown.join(", ") + (pending.length > max ? ` và ${pending.length - max} chỉ tiêu khác` : "");
}
